import React from 'react'
import ConfiguratorAppContext from '../ConfiguratorAppContext'
import { LoadingState } from '../utils/LoadingState'
import { CashSalesAddToCartResponse, CashSalesApi } from '../CashSalesApi'
import { AppStateActions } from '../state/AppState'
import { logDebug, logErrorDebug } from '../debug/debugLogging'
import { CtaSpec, ListCtaSection } from './ListCtaSection'
import { BuildConfig } from '../config/BuildConfig'
import { IconDownloadGreen } from '../components/icons'
import { PUBLIC_URL } from '../environment'
import { isNotNull } from '../utils/IsNotNull'
import Button from '../components/Button'
import SpinnerPopup from '../components/SpinnerPopup'
import CashSalesResultsPopup from './CashSalesResultsPopup'
import CashSalesAddToCartConfirmPopup from './CashSalesAddToCartConfirmPopup'
import SharedListLinkPopup from './SharedListLinkPopup'
import { InflatedListItemProduct } from './ListTypes'
import { BomItem } from '../bom/bomUtils'
import { Alert } from '../utils/Alert'
import { AnalyticsContext } from '../analytics/AnalyticsContext'

export interface StandardListCtaSectionProps {
  configuredProducts: readonly InflatedListItemProduct[]
  fullListBom: readonly BomItem[]
  showAlert: (alert: Alert) => void
  saveList: () => Promise<string>
  onClickEmailList: () => void
  onClickDownloadList: () => void
}

export function StandardListCtaSection({
  configuredProducts,
  fullListBom,
  showAlert,
  saveList,
  onClickEmailList,
  onClickDownloadList,
}: StandardListCtaSectionProps) {
  const {
    dispatch,
    userType,
    debugMode,
    shouldShowCashSales,
  } = React.useContext(ConfiguratorAppContext)

  const analyticsLogger = React.useContext(AnalyticsContext)

  // CTAs are hidden for cash sales users
  const shouldShowCtas = userType !== null && !shouldShowCashSales

  const isListEmpty = configuredProducts.length === 0

  const [cashSalesLoadingState, setCashSalesLoadingState] = React.useState<LoadingState | null>(null)

  const [debugModeSharedListId, setDebugModeSharedListId] = React.useState<string | null>(null)

  const [cashSalesAddToCartPopupShown, setCashSalesAddToCartPopupShown] = React.useState(false)
  const [cashSalesResponse, setCashSalesResponse] = React.useState<CashSalesAddToCartResponse | null>(null)

  const isElectricianUser = userType === 'electrician'

  const mailingListAddress = isElectricianUser ? BuildConfig.brand.tradeMailingListAddress : BuildConfig.brand.mailingListAddress

  // trade and cash sales users get CSV button
  const shouldShowDownloadButton = isElectricianUser || shouldShowCashSales

  let onClickAddToCashSalesCart: (() => void) | null = null

  if (process.env.REACT_APP_ENABLE_CASH_SALES_INTEGRATION === 'true') {
    // REACT_APP_ENABLE_CASH_SALES_INTEGRATION is constant at build-time
    // Webpack tree-shaking will remove the conditional
    // eslint-disable-next-line react-hooks/rules-of-hooks
    React.useEffect(() => {
      CashSalesApi.getStatus()
        .then(it => {
          dispatch({ action: AppStateActions.CashSalesVisibilityChanged, visible: it.isCashsalesUser })
          logDebug('CashSales', 'cash sales logged in:', it)
        })
        .catch((e) => {
          logDebug('CashSales', 'cash sales unavailable:', e)
        })
    }, [dispatch])

    onClickAddToCashSalesCart = () => {
      const bom = fullListBom

      if (bom.length > 0) {
        setCashSalesLoadingState({ message: 'Adding to cart...' })
        CashSalesApi.addCartItems(bom.map(bomItem => ({
          itemNumber: bomItem.itemNo,
          quantity: bomItem.quantity,
        })))
          .then(res => {
            setCashSalesResponse(res)
          })
          .catch(e => {
            logErrorDebug('error adding to cart', e)
            showAlert({ message: 'Something went wrong adding to your cart.', buttonLabel: 'Back' })
          })
          .finally(() => {
            setCashSalesLoadingState(null)
          })
      }
    }
  }

  const ctaButtons: (CtaSpec | null)[] = [
    {
      style: 'green',
      disabled: isListEmpty,
      onClick: () => onClickEmailList(),
      text: 'Email your List',
      id: 'email-list',
    },

    debugMode ? ({
      style: 'debug',
      disabled: isListEmpty,
      onClick: () => {
        if (!cashSalesLoadingState) {
          setCashSalesLoadingState({ message: 'Saving your list...' })
          saveList()
            .then(id => {
              setDebugModeSharedListId(id)
            })
            .finally(() => {
              setCashSalesLoadingState(null)
            })
        }
      },
      text: 'Share your List',
    }) : null,

    (shouldShowCashSales && onClickAddToCashSalesCart) ? ({
      text: BuildConfig.brand.copy.ButtonAddToCashSalesCart,
      style: 'green',
      disabled: isListEmpty,
      onClick: () => setCashSalesAddToCartPopupShown(true),
      id: 'cash-sales',
    }) : null,

    shouldShowDownloadButton ? ({
      disabled: isListEmpty,
      onClick: () => {
        analyticsLogger.log({
          event: 'cta_click',
          cta_name: isElectricianUser ? BuildConfig.brand.copy.ButtonDownloadElectrician : BuildConfig.brand.copy.ButtonDownload,
          cta_link: null,
          cta_category: 'Button',
          cta_location: 'My list'
        })

        analyticsLogger.log({
          event: 'file_download',
          document_id: null,
          document_name: 'Iconic Export',
          document_type: 'csv'
        })

        onClickDownloadList()
      },
      text: (
        <>
          <img src={IconDownloadGreen} alt="" style={{ marginRight: '10px' }}/>
          {isElectricianUser ? BuildConfig.brand.copy.ButtonDownloadElectrician : BuildConfig.brand.copy.ButtonDownload}
        </>
      ),
      id: 'download-list',
    }) : null,

    ...(
      shouldShowCtas ? BuildConfig.brand.ctas[userType]
        .map<CtaSpec>(({ label, url, analyticsLabel, id }, index) => (
        {
          paddingBefore: index === 0,
          onClick: () => {
            analyticsLogger.log({
              event: 'cta_click',
              cta_name: label,
              cta_link: (typeof url === 'string') ? url : null,
              cta_category: 'Button',
              cta_location: 'My list'
            })

            if (typeof url === 'string') {
              window.open(url)
            } else {
              setCashSalesLoadingState({ message: 'Saving your list...' })
              saveList()
                .then(listId => {
                  const shareUrl = `${window.location.origin}${PUBLIC_URL}/list/${listId}`
                  window.open(url(shareUrl))
                })
                .catch((e) => {
                  console.error('error opening cta', e)
                  showAlert({ message: 'Something went wrong..', buttonLabel: 'Back' })
                })
                .finally(() => {
                  setCashSalesLoadingState(null)
                })
            }
          },
          text: label,
          id: id,
        })) : []
    ),
  ]

  const filteredCtas: readonly CtaSpec[] = ctaButtons
    .filter(isNotNull)

  return (
    <>
      {userType && (
        <ListCtaSection
          ctas={filteredCtas}
        />
      )}

      {
        shouldShowCtas ? (
          <>
            <div className="footer-container">
              <div className="footer">
                <h3>We&apos;ll give you ideas</h3>
                <p>Sign up if you&apos;d like to receive news and commercial info from Schneider Electric.</p>
                <Button
                  buttonStyle="green"
                  onClick={() => {
                    analyticsLogger.log({
                      event: 'cta_click',
                      cta_name: 'Join mailing list',
                      cta_link: mailingListAddress,
                      cta_category: 'Button',
                      cta_location: 'My list',
                    })

                    window.open(mailingListAddress)
                  }}
                >
                  Join mailing list
                </Button>
              </div>
            </div>
          </>
        ) : (
          <div style={{ paddingTop: '20px' }}/>
        )
      }

      {cashSalesLoadingState && (
        <SpinnerPopup>
          {cashSalesLoadingState.message}
        </SpinnerPopup>
      )}

      {cashSalesResponse && (
        <CashSalesResultsPopup
          onClose={() => setCashSalesResponse(null)}
          response={cashSalesResponse}
        />
      )}

      {cashSalesAddToCartPopupShown && onClickAddToCashSalesCart && (
        <CashSalesAddToCartConfirmPopup
          onCancel={() => setCashSalesAddToCartPopupShown(false)}
          onConfirm={() => {
            setCashSalesAddToCartPopupShown(false)
            onClickAddToCashSalesCart?.()
          }}
          bom={fullListBom}
        />
      )}

      {
        debugModeSharedListId && (
          <SharedListLinkPopup
            onClose={() => setDebugModeSharedListId(null)}
            id={debugModeSharedListId}
          />
        )
      }
    </>
  )
}